import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt, faSyncAlt } from "@fortawesome/free-solid-svg-icons";

import api from "../../api/http";
import "../../styles/admin/TermeniManager.css"; // ✅ păstrăm fișierul, dar refacem clasele unice

const TermeniManager = () => {
  const [termeni, setTermeni] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [selectedTermen, setSelectedTermen] = useState(null);

  const [form, setForm] = useState({
    titlu: "",
    descrierea: "",
    text: "",
  });

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const isLoggedIn = Boolean(sessionStorage.getItem("access"));

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(""), 5000);
    return () => clearTimeout(t);
  }, [message]);

  const parseApiError = (err) => {
    const data = err?.response?.data;
    if (!data) return err?.message || "A apărut o eroare.";
    if (typeof data === "string") return data;
    if (Array.isArray(data)) return data[0] || "A apărut o eroare.";
    if (data.detail) return data.detail;

    if (typeof data === "object") {
      const k = Object.keys(data)[0];
      const v = data[k];
      if (Array.isArray(v)) return v[0];
      if (typeof v === "string") return v;
    }
    return "A apărut o eroare.";
  };

  const normalizeList = (data) => {
    if (Array.isArray(data?.results)) return data.results;
    if (Array.isArray(data)) return data;
    return [];
  };

  const fetchTermeni = async () => {
    setError("");
    try {
      const res = await api.get("/about/terminii/");
      setTermeni(normalizeList(res.data));
    } catch (err) {
      setTermeni([]);
      setError(parseApiError(err));
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchTermeni();
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAdd = () => {
    setMessage("");
    setError("");
    setSelectedTermen(null);
    setForm({ titlu: "", descrierea: "", text: "" });
    setShowForm(true);
  };

  const openEdit = (termen) => {
    setMessage("");
    setError("");
    setSelectedTermen(termen);
    setForm({
      titlu: termen?.titlu || "",
      descrierea: termen?.descrierea || "",
      text: termen?.text || "",
    });
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setSelectedTermen(null);
    setForm({ titlu: "", descrierea: "", text: "" });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const submit = async () => {
    setMessage("");
    setError("");

    const titlu = form.titlu.trim();
    const descrierea = form.descrierea.trim();
    const text = String(form.text ?? "").trim();

    if (!titlu) {
      setError("Titlul este obligatoriu.");
      return;
    }
    if (!descrierea) {
      setError("Descrierea este obligatorie.");
      return;
    }

    const payload = { titlu, descrierea, text: text === "" ? null : text };

    setSaving(true);
    try {
      if (selectedTermen) {
        await api.put(`/about/terminii/${selectedTermen.id}/`, payload);

        setTermeni((prev) =>
          prev.map((t) => (t.id === selectedTermen.id ? { ...t, ...payload } : t))
        );

        setMessage("Termen actualizat cu succes.");
      } else {
        await api.post("/about/terminii/create/", payload);
        setMessage("Termen adăugat cu succes.");
      }

      closeForm();
      await fetchTermeni();
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const deleteTermen = async (id) => {
    setMessage("");
    setError("");

    const ok = window.confirm("Sigur vrei să ștergi acest termen?");
    if (!ok) return;

    setDeletingId(id);
    try {
      await api.delete(`/about/terminii/${id}/`);
      setTermeni((prev) => prev.filter((t) => t.id !== id));
      setMessage("Termen șters.");
      await fetchTermeni();
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setDeletingId(null);
    }
  };

  const onFormKeyDown = (e) => {
    if (e.key === "Escape") closeForm();
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) submit();
  };

  return (
    <div className="sr-termeniMng">

      {message && <div className="sr-termeniMng__msg sr-termeniMng__msg--ok">{message}</div>}
      {error && <div className="sr-termeniMng__msg sr-termeniMng__msg--err">{error}</div>}

      {/* FORM */}
      {showForm ? (
        <form
          onSubmit={(e) => e.preventDefault()}
          className="sr-termeniMng__form"
          onKeyDown={onFormKeyDown}
        >
          <textarea
            className="sr-termeniMng__textarea"
            name="titlu"
            value={form.titlu}
            placeholder="Titlu"
            onChange={handleChange}
            disabled={saving}
          />
          <textarea
            className="sr-termeniMng__textarea"
            name="descrierea"
            value={form.descrierea}
            placeholder="Descriere"
            onChange={handleChange}
            disabled={saving}
          />
          <textarea
            className="sr-termeniMng__textarea"
            name="text"
            value={form.text}
            placeholder="Text (opțional)"
            onChange={handleChange}
            disabled={saving}
          />

          <div className="sr-termeniMng__formActions">
            <button
              type="button"
              className="sr-termeniMng__btn sr-termeniMng__btn--primary"
              onClick={submit}
              disabled={saving}
            >
              {saving ? "Se salvează..." : selectedTermen ? "Actualizează" : "Adaugă"}
            </button>

            <button
              type="button"
              className="sr-termeniMng__btn sr-termeniMng__btn--danger"
              onClick={closeForm}
              disabled={saving}
            >
              Close
            </button>
          </div>
        </form>
      ) : (
        isLoggedIn && (
          <button className="sr-termeniMng__btn sr-termeniMng__btn--primary" onClick={openAdd}>
            Adaugă termen nou
          </button>
        )
      )}

      {/* LIST */}
      {loading ? (
        <p className="sr-termeniMng__loading">Loading...</p>
      ) : (
        <ul className="sr-termeniMng__list">
          {termeni.map((termen) => (
            <li key={termen.id} className="sr-termeniMng__item">
              <h3 className="sr-termeniMng__itemTitle">* {termen.titlu}</h3>
              <p className="sr-termeniMng__text">{termen.descrierea}</p>
              {termen.text && <p className="sr-termeniMng__text2">{termen.text}</p>}

              {isLoggedIn && (
                <div className="sr-termeniMng__actions">
                  <button
                    className="sr-termeniMng__iconBtn sr-termeniMng__iconBtn--danger"
                    title="Șterge"
                    onClick={() => deleteTermen(termen.id)}
                    disabled={deletingId === termen.id}
                    type="button"
                  >
                    <FontAwesomeIcon icon={faTrashAlt} />
                  </button>

                  <button
                    className="sr-termeniMng__iconBtn sr-termeniMng__iconBtn--ok"
                    title="Editează"
                    onClick={() => openEdit(termen)}
                    disabled={saving || deletingId === termen.id}
                    type="button"
                  >
                    <FontAwesomeIcon icon={faSyncAlt} />
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default TermeniManager;